import { Node, mergeAttributes } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';

export interface MentionItem {
  id: string;
  label: string;
  type: 'appliance' | 'ingredient';
}

export const SharedMention = Node.create({
  name: 'mention',

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      suggestion: {
        char: '@',
        pluginKey: new PluginKey('sharedMention'),
        allowSpaces: false,
        allowedPrefixes: [' ', '\n'],
        startOfLine: false,
        items: () => [],
        command: ({
          editor,
          range,
          props,
        }: {
          editor: Editor;
          range: { from: number; to: number };
          props: { id: string };
        }) => {
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: 'mention',
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();
        },
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {};
          }
          return {
            'data-id': attributes.id,
          };
        },
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {};
          }
          return {
            'data-label': attributes.label,
          };
        },
      },
      type: {
        default: 'ingredient',
        parseHTML: (element) => element.getAttribute('data-mention-type'),
        renderHTML: (attributes) => {
          if (!attributes.type) {
            return {};
          }
          return {
            'data-mention-type': attributes.type,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-mention-type]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const mentionType = HTMLAttributes.type || 'ingredient';
    const cssClass =
      mentionType === 'appliance' ? 'appliance-mention' : 'ingredient-mention';

    return [
      'span',
      mergeAttributes(
        { 'data-mention-type': mentionType },
        this.options.HTMLAttributes,
        HTMLAttributes,
        { class: `mention ${cssClass}` },
      ),
      `@${HTMLAttributes.label || HTMLAttributes.id}`,
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('span');
      const mentionType = node.attrs.type || 'ingredient';
      const cssClass =
        mentionType === 'appliance'
          ? 'appliance-mention'
          : 'ingredient-mention';

      dom.className = `mention ${cssClass}`;
      dom.setAttribute('data-mention-type', mentionType);
      dom.setAttribute('data-id', node.attrs.id);
      dom.setAttribute('data-label', node.attrs.label);
      dom.textContent = `@${node.attrs.label || node.attrs.id}`;

      // Add hover card functionality
      const showHoverCard = () => {
        const tooltip = document.createElement('div');
        tooltip.className = 'mention-tooltip';
        tooltip.innerHTML = `
          <div class="tooltip-content">
            <div class="tooltip-type">${mentionType}</div>
            <div class="tooltip-label">${node.attrs.label || node.attrs.id}</div>
            ${
              mentionType === 'appliance'
                ? '<div class="tooltip-description">Kitchen appliance for cooking</div>'
                : '<div class="tooltip-description">Recipe ingredient</div>'
            }
          </div>
        `;

        document.body.appendChild(tooltip);

        const rect = dom.getBoundingClientRect();
        tooltip.style.position = 'fixed';
        tooltip.style.top = `${rect.bottom + 8}px`;
        tooltip.style.left = `${rect.left}px`;
        tooltip.style.zIndex = '1000';

        const hideTooltip = () => {
          if (tooltip && tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
          }
          dom.removeEventListener('mouseleave', hideTooltip);
        };

        dom.addEventListener('mouseleave', hideTooltip);

        // Auto-hide after 3 seconds
        window.setTimeout(hideTooltip, 3000);
      };

      dom.addEventListener('mouseenter', showHoverCard);

      return { dom };
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
